/**
 * Provider Icon Component
 *
 * Displays an icon representing the email provider (Gmail, Outlook, Custom SMTP).
 * Uses Mantine ThemeIcon for consistent styling.
 */

import { ThemeIcon, ThemeIconProps, Tooltip } from "@mantine/core";
import { IconMail, IconBrandGmail, IconBrandOffice } from "@tabler/icons-react";
import type { EmailProvider } from "../../schema/email-account.types";

/**
 * Provider configuration mapping
 */
const PROVIDER_CONFIG: Record<
  EmailProvider,
  {
    name: string;
    Icon: typeof IconMail;
    color: string;
  }
> = {
  gmail: {
    name: "Gmail",
    Icon: IconBrandGmail,
    color: "red"
  },
  outlook: {
    name: "Outlook",
    Icon: IconBrandOffice,
    color: "blue"
  },
  custom: {
    name: "Custom SMTP",
    Icon: IconMail,
    color: "gray"
  }
};

/**
 * Props for ProviderIcon
 */
export interface ProviderIconProperties {
  /** Email provider type */
  provider: EmailProvider;
  /** Optional icon size */
  size?: ThemeIconProps["size"];
  /** Optional icon radius */
  radius?: ThemeIconProps["radius"];
  /** Show tooltip with provider name */
  showTooltip?: boolean;
}

/**
 * ProviderIcon Component
 * @param root0
 * @param root0.provider
 * @param root0.size
 * @param root0.radius
 * @param root0.showTooltip
 * @example
 * <ProviderIcon provider="gmail" />
 * <ProviderIcon provider="outlook" size="lg" showTooltip />
 */
export function ProviderIcon({ provider, size = "md", radius = "sm", showTooltip = true }: ProviderIconProperties) {
  const config = PROVIDER_CONFIG[provider];
  const { Icon, color, name } = config;

  const icon = (
    <ThemeIcon size={size} radius={radius} color={color} variant="light">
      <Icon size={16} />
    </ThemeIcon>
  );

  if (showTooltip) {
    return <Tooltip label={name}>{icon}</Tooltip>;
  }

  return icon;
}

ProviderIcon.displayName = "ProviderIcon";
